<script setup>
import { ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { format, parseISO } from 'date-fns';
import dealReportsAPI from 'dashboard/api/dealReports';
import BarChart from 'shared/components/charts/BarChart.vue';
import LineChart from 'shared/components/charts/DashboardLineChart.vue';
import TableHeaderCell from './TableHeaderCell.vue';
import { formatCurrencyBRL } from './commercialFormatters';
import {
  barDataset,
  barOptions,
  lineDataset,
  lineOptions,
} from '../charts/chartDesign';
import { useChartTheme } from '../charts/useChartTheme';

const props = defineProps({
  params: { type: Object, required: true },
});

const { t } = useI18n();
const data = ref(null);
const isLoading = ref(true);
const hasError = ref(false);
const chartType = ref('line');
const theme = useChartTheme();

const fetchData = async () => {
  isLoading.value = true;
  hasError.value = false;
  try {
    const response = await dealReportsAPI.getEvolution(props.params);
    data.value = response.data;
  } catch {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

watch(() => props.params, fetchData, { immediate: true, deep: true });

const series = computed(() => data.value?.series || []);

const chartCollection = computed(() => {
  const dataset = {
    data: series.value.map(row => row.revenue),
    label: t('DASHBOARD.COMERCIAL.EVOLUTION.REVENUE'),
  };
  return {
    labels: series.value.map(row => format(parseISO(row.date), 'dd/MM')),
    datasets: [
      chartType.value === 'line'
        ? lineDataset({ ...dataset, theme })
        : barDataset({ ...dataset, horizontal: false }),
    ],
  };
});

const chartOptions = computed(() =>
  chartType.value === 'line'
    ? lineOptions({ theme: theme.value, formatTick: formatCurrencyBRL })
    : barOptions({
        theme: theme.value,
        formatTick: formatCurrencyBRL,
        horizontal: false,
      })
);

const chartComponent = computed(() =>
  chartType.value === 'line' ? LineChart : BarChart
);
</script>

<template>
  <section class="border rounded-lg border-n-weak">
    <div class="flex items-center justify-between p-3">
      <h2 class="font-medium text-n-slate-12">
        {{ t('DASHBOARD.COMERCIAL.SECTIONS.EVOLUTION') }}
      </h2>
      <div class="flex gap-1">
        <button
          type="button"
          class="p-1.5 rounded-md"
          :class="
            chartType === 'line'
              ? 'bg-n-brand text-white'
              : 'text-n-slate-11 hover:bg-n-alpha-1'
          "
          :aria-label="t('DASHBOARD.COMERCIAL.EVOLUTION.CHART_TYPE_LINE')"
          @click="chartType = 'line'"
        >
          <span class="i-lucide-line-chart size-4 block" />
        </button>
        <button
          type="button"
          class="p-1.5 rounded-md"
          :class="
            chartType === 'bar'
              ? 'bg-n-brand text-white'
              : 'text-n-slate-11 hover:bg-n-alpha-1'
          "
          :aria-label="t('DASHBOARD.COMERCIAL.EVOLUTION.CHART_TYPE_BAR')"
          @click="chartType = 'bar'"
        >
          <span class="i-lucide-bar-chart-3 size-4 block" />
        </button>
      </div>
    </div>

    <p v-if="hasError" class="px-3 pb-3 text-sm text-n-ruby-9">
      {{ t('DASHBOARD.COMERCIAL.LOAD_ERROR') }}
      <button type="button" class="underline" @click="fetchData">
        {{ t('DASHBOARD.COMERCIAL.RETRY') }}
      </button>
    </p>
    <p v-else-if="isLoading" class="px-3 pb-3 text-sm text-n-slate-11">
      {{ t('DASHBOARD.COMERCIAL.LOADING') }}
    </p>
    <div v-else class="px-3 pb-3">
      <p v-if="!series.length" class="py-2 text-sm text-n-slate-10">
        {{ t('DASHBOARD.COMERCIAL.EMPTY_STATE') }}
      </p>
      <template v-else>
        <div class="h-64">
          <component
            :is="chartComponent"
            :collection="chartCollection"
            :chart-options="chartOptions"
          />
        </div>
        <div class="mt-3 overflow-y-auto overflow-x-auto max-h-80">
          <table class="w-full text-sm">
            <thead>
              <tr class="text-left text-n-slate-10">
                <th class="py-1 pr-3">
                  <TableHeaderCell
                    :label="t('DASHBOARD.COMERCIAL.EVOLUTION.DATE')"
                    :tooltip="t('DASHBOARD.COMERCIAL.TOOLTIPS.EVOLUTION_DATE')"
                  />
                </th>
                <th class="py-1 pr-3">
                  <TableHeaderCell
                    :label="t('DASHBOARD.COMERCIAL.EVOLUTION.REVENUE')"
                    :tooltip="t('DASHBOARD.COMERCIAL.TOOLTIPS.REVENUE')"
                  />
                </th>
                <th class="py-1 pr-3">
                  <TableHeaderCell
                    :label="t('DASHBOARD.COMERCIAL.EVOLUTION.WON_COUNT')"
                    :tooltip="t('DASHBOARD.COMERCIAL.TOOLTIPS.WON_COUNT')"
                  />
                </th>
                <th class="py-1 pr-3">
                  <TableHeaderCell
                    :label="t('DASHBOARD.COMERCIAL.EVOLUTION.CREATED_COUNT')"
                    :tooltip="t('DASHBOARD.COMERCIAL.TOOLTIPS.CREATED_COUNT')"
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in series"
                :key="row.date"
                class="border-t border-n-weak"
              >
                <td class="py-1 pr-3 text-n-slate-12">
                  {{ format(parseISO(row.date), 'dd/MM/yyyy') }}
                </td>
                <td class="py-1 pr-3">{{ formatCurrencyBRL(row.revenue) }}</td>
                <td class="py-1 pr-3">{{ row.won_count }}</td>
                <td class="py-1 pr-3">{{ row.created_count }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </div>
  </section>
</template>
